import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 3: Data Backup & Restore Automation
 * - RDS + Mongo sources -> EC2 backup server (scripts + Liquibase or MongoBee + schema ver)
 * - Validation layer (Docker -> Validate)
 * - Upload to S3/Blob
 * - CloudWatch monitoring
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 8,
    fontWeight: 800,
    color: "#0f172a",
    width: 240,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0b2e59",
    width: 150,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#4a0b2a",
    width: 150,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0f3d1f",
    width: 150,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#05333b",
    width: 120,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 8,
    fontWeight: 700,
    color: "#92400e",
    width: 200,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a", fontSize: 12 },
    labelShowBg: true,
    labelBgStyle: { fill: "#ffffff", stroke: "#e2e8f0", strokeWidth: 1 },
    labelBgPadding: [8, 5],
    labelBgBorderRadius: 6,
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project3FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background containers
      {
        id: "bg-ec2",
        position: { x: 260, y: 120 },
        data: { label: "" },
        style: {
          width: 840,
          height: 380,
          background: "#f0fdf420",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-validation",
        position: { x: 320, y: 295 },
        data: { label: "" },
        style: {
          width: 700,
          height: 190,
          background: "transparent",
          border: "2px dashed #22c55e",
          borderRadius: 12,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-storage",
        position: { x: 240, y: 540 },
        data: { label: "" },
        style: {
          width: 760,
          height: 150,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Source databases
      {
        id: "src-rds",
        position: { x: 60, y: 40 },
        data: { label: "RDS PostgreSQL" },
        style: nodeStyle.data,
      },
      {
        id: "src-mongo",
        position: { x: 60, y: 120 },
        data: { label: "MongoDB" },
        style: nodeStyle.data,
      },
      {
        id: "extract",
        position: { x: 60, y: 220 },
        data: { label: "Extract Schema" },
        style: nodeStyle.note,
      },

      // EC2 backup server area
      {
        id: "sec-ec2",
        position: { x: 300, y: 135 },
        data: { label: "EC2 BACKUP SERVER - AWS VPC" },
        style: { ...nodeStyle.section, width: 300 },
      },
      {
        id: "backup",
        position: { x: 300, y: 210 },
        data: { label: "Backup Scripts" },
        style: nodeStyle.ops,
      },
      {
        id: "liquibase",
        position: { x: 520, y: 210 },
        data: { label: "Liquibase or MongoBee" },
        style: nodeStyle.compute,
      },
      {
        id: "schema",
        position: { x: 760, y: 210 },
        data: { label: "Schema Version" },
        style: nodeStyle.compute,
      },

      // Validation layer
      {
        id: "sec-validation",
        position: { x: 505, y: 310 },
        data: { label: "② VALIDATION" },
        style: { ...nodeStyle.section, width: 280 },
      },
      {
        id: "docker",
        position: { x: 390, y: 372 },
        data: { label: "Docker" },
        style: { ...nodeStyle.compute, width: 130, padding: 8 },
      },
      {
        id: "validate",
        position: { x: 570, y: 372 },
        data: { label: "Validate" },
        style: { ...nodeStyle.ops, width: 130, padding: 8 },
      },
      {
        id: "data-mode",
        position: { x: 820, y: 372 },
        data: { label: "Structured +\nUnstructured" },
        style: { ...nodeStyle.ops, width: 170, padding: 8 },
      },

      // Supporting services
      {
        id: "vpc",
        position: { x: 320, y: 455 },
        data: { label: "VPC" },
        style: nodeStyle.net,
      },
      {
        id: "sg",
        position: { x: 470, y: 455 },
        data: { label: "SG" },
        style: nodeStyle.net,
      },
      {
        id: "iam",
        position: { x: 620, y: 455 },
        data: { label: "IAM" },
        style: nodeStyle.net,
      },

      // Storage targets
      {
        id: "sec-storage",
        position: { x: 260, y: 550 },
        data: { label: "③ UPLOAD BACKUPS" },
        style: { ...nodeStyle.section, width: 240, borderColor: "#fb923c" },
      },
      {
        id: "s3",
        position: { x: 300, y: 610 },
        data: { label: "AWS S3" },
        style: nodeStyle.ops,
      },
      {
        id: "blob",
        position: { x: 500, y: 610 },
        data: { label: "Azure Blob" },
        style: nodeStyle.compute,
      },
      {
        id: "gcs",
        position: { x: 700, y: 610 },
        data: { label: "GCP Storage" },
        style: nodeStyle.compute,
      },

      // Monitoring
      {
        id: "cw",
        position: { x: 820, y: 560 },
        data: { label: "CloudWatch" },
        style: nodeStyle.ops,
      },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const purple = { stroke: "#7c3aed" };
    const green = { stroke: "#16a34a" };
    const orange = { stroke: "#f59e0b" };

    return [
      mkEdge("e-rds-extract", "src-rds", "extract", "① Extract Schema", {
        animated: true,
        style: { ...blue, strokeWidth: 5 },
      }),
      mkEdge("e-mongo-extract", "src-mongo", "extract", "① Extract Schema", {
        animated: true,
        style: { ...blue, strokeWidth: 5 },
      }),
      mkEdge("e-extract-backup", "extract", "backup", "Run Backup", {
        style: { ...blue, strokeWidth: 4 },
      }),
      mkEdge("e-backup-liquibase", "backup", "liquibase", "Version", {
        style: { ...purple, strokeWidth: 4 },
      }),
      mkEdge("e-liquibase-schema", "liquibase", "schema", "Track", {
        style: { ...purple, strokeWidth: 4 },
      }),
      mkEdge("e-backup-docker", "backup", "docker", "② Validation", {
        style: { ...green, strokeWidth: 4 },
      }),
      mkEdge("e-docker-validate", "docker", "validate", "Test", {
        style: { ...green, strokeWidth: 4 },
      }),
      mkEdge("e-validate-mode", "validate", "data-mode", "SQL + NoSQL", {
        style: { ...green, strokeWidth: 4 },
      }),
      mkEdge("e-backup-upload", "backup", "s3", "③ Upload Backups", {
        animated: true,
        style: { ...orange, strokeWidth: 5, strokeDasharray: "10 8" },
      }),
      mkEdge("e-backup-upload-azure", "backup", "blob", "③ Upload Backups", {
        animated: true,
        style: { ...orange, strokeWidth: 5, strokeDasharray: "10 8" },
      }),
      mkEdge("e-backup-upload-gcs", "backup", "gcs", "③ Upload Backups", {
        animated: true,
        style: { ...orange, strokeWidth: 5, strokeDasharray: "10 8" },
      }),
      mkEdge("e-backup-cw", "backup", "cw", "Monitoring", {
        style: { ...green, strokeWidth: 4 },
      }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "760px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.4}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
